// src/lib/pricing-block.js — Phase 6.B PR5A (ADR-096)
//
// ⚠️ DUPLICATION CONTRÔLÉE — Pattern ADR-021
//
// Ce fichier est la version ESM de lib/pricing-block.js (CJS).
// Toute modification DOIT être appliquée AUX DEUX fichiers (CJS + ESM).
// Cf. ADR-021 dans docs/handoff/architecture/decisions-log.md.
//
// Helpers de blocage multi-dates (numAvail=0) Beds24 (Phase 6.B PR5A).

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Valide ISO YYYY-MM-DD strict + roundtrip (refuse 2026-02-30).
 *
 * @param {string} str
 * @returns {boolean}
 */
export function isValidISODate(str) {
  if (typeof str !== "string" || !ISO_DATE_REGEX.test(str)) return false;
  const d = new Date(`${str}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  return d.toISOString().slice(0, 10) === str;
}

/**
 * LPR3-1 — arithmétique timestamps (immune DST).
 *
 * @param {string} from ISO YYYY-MM-DD
 * @param {string} to ISO YYYY-MM-DD
 * @returns {number} nombre de jours from → to (signé)
 */
export function daysBetween(from, to) {
  const ms =
    new Date(`${to}T00:00:00Z`).getTime() -
    new Date(`${from}T00:00:00Z`).getTime();
  return Math.floor(ms / 86400000);
}

/**
 * Soustrait 1 jour à une date ISO YYYY-MM-DD (timestamps UTC, immune DST).
 *
 * Utilisé par buildBlockRanges (C51 ADR-111) pour convertir end_date Convention A
 * (exclusif, jour de départ libre) → to inclusif pour API Beds24.
 *
 * @param {string} iso YYYY-MM-DD
 * @returns {string|null} YYYY-MM-DD ou null si input invalide
 */
export function dateMinusOneDay(iso) {
  if (!isValidISODate(iso)) return null;
  const ms = new Date(`${iso}T00:00:00Z`).getTime() - 86400000;
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Construit le payload `ranges` Beds24 pour un block sur une période continue.
 *
 * Convention Beds24 `POST /inventory/rooms/calendar` : `numAvail=0` ferme
 * la dispo, `numAvail=1` la rouvre. `from`/`to` Beds24 = **inclusif sur les
 * deux bornes** (chaque jour entre from et to inclus est mis à `numAvail`).
 *
 * C51 ADR-111 — Convention A : `end_date` côté DB FBZ est EXCLUSIF
 * (jour de départ libre, standard Airbnb/Booking/Beds24). On convertit en
 * inclusif côté Beds24 push via `to = end_date - 1 jour`.
 *
 * Ex : block 15→16 mai (1 nuit Convention A) → Beds24 ranges = {from:15, to:15}
 *      = bloque la nuit du 15 uniquement (numAvail=0 sur le 15).
 *
 * @param {string} startDate ISO YYYY-MM-DD (inclusif, jour d'arrivée)
 * @param {string} endDate ISO YYYY-MM-DD (Convention A exclusif, jour de départ)
 * @param {number} numAvail 0 (block_create) | 1 (block_release)
 * @returns {Array<{from:string, to:string, numAvail:number}>}
 */
export function buildBlockRanges(startDate, endDate, numAvail) {
  if (!isValidISODate(startDate) || !isValidISODate(endDate)) return [];
  if (startDate >= endDate) return [];
  const n = Number(numAvail);
  if (!Number.isFinite(n) || (n !== 0 && n !== 1)) return [];
  const toBeds24 = dateMinusOneDay(endDate);
  if (!toBeds24) return [];
  return [{ from: startDate, to: toBeds24, numAvail: n }];
}

/**
 * Valide une paire (start_date, end_date) côté endpoint.
 *
 * MPR5-5 : horizon Beds24 strict 540j (cohérent cheatsheet).
 *
 * @param {string} start ISO YYYY-MM-DD
 * @param {string} end ISO YYYY-MM-DD
 * @param {string} today ISO YYYY-MM-DD (MPR5-8 timezone Europe/Paris côté caller)
 * @param {number} [horizonDays=540]
 * @returns {{ok:boolean, error?:string}}
 */
export function validateBlockDates(start, end, today, horizonDays = 540) {
  if (!isValidISODate(start)) return { ok: false, error: "start_date_invalid" };
  if (!isValidISODate(end)) return { ok: false, error: "end_date_invalid" };
  if (today && !isValidISODate(today))
    return { ok: false, error: "today_invalid" };
  if (today && start < today) return { ok: false, error: "start_date_past" };
  // C51 ADR-111 — Convention A : end_date strictement après start_date
  // (1 nuit = end = start + 1 jour). end == start refusé (data pourrie).
  if (end <= start) return { ok: false, error: "date_range_inverted" };
  if (daysBetween(start, end) > horizonDays) {
    return { ok: false, error: "horizon_too_large" };
  }
  return { ok: true };
}

/**
 * Indique si deux périodes (blocks) se chevauchent.
 *
 * Convention A stricte (ADR-111) — bords exclusifs : `end_date` est le jour
 * de départ libre (standard Airbnb/Booking/Beds24). Deux blocks dos-à-dos
 * (blockA.end_date == blockB.start_date) NE se chevauchent PAS — check-out
 * du jour J et check-in du jour J coexistent (dos-à-dos autorisé).
 *
 * Fix R-1 (migration 20260717000001) : opérateurs `<` et `>` remplacent
 * `<=` et `>=`. Aligné sur RPC Postgres `create_pricing_block` étape 1
 * et `validate_booking_request` étape 5 (mêmes opérateurs stricts).
 *
 * INVARIANT : ne pas revenir à `<=` / `>=` sans ADR explicite — ce serait
 * re-casser le fix et désaligner JS ↔ SQL.
 *
 * @param {{start_date:string, end_date:string}} blockA
 * @param {{start_date:string, end_date:string}} blockB
 * @returns {boolean}
 */
export function isOverlap(blockA, blockB) {
  if (!blockA || !blockB) return false;
  if (
    !isValidISODate(blockA.start_date) ||
    !isValidISODate(blockA.end_date) ||
    !isValidISODate(blockB.start_date) ||
    !isValidISODate(blockB.end_date)
  ) {
    return false;
  }
  return (
    blockA.start_date < blockB.end_date && blockA.end_date > blockB.start_date
  );
}

/**
 * Diff différentiel des dates d'un bloc (Convention A ADR-111) — LOT 1
 * pricing_block_edit_dates. Calcule les zones de NUITS dont le statut de dispo
 * change entre l'ancienne plage [oldStart, oldEnd) et la nouvelle [newStart,
 * newEnd), toutes deux EXCLUSIVES sur `end`.
 *
 * Espace EXCLUSIF strict (aucune nuit dans deux zones à la fois quand les plages
 * se chevauchent/se touchent) :
 *   - newStart < oldStart → le bloc s'étend en amont → closeZones += [newStart, oldStart)
 *   - newStart > oldStart → le bloc se réduit en amont → reopenZones += [oldStart, newStart)
 *   - newEnd   > oldEnd   → le bloc s'étend en aval  → closeZones += [oldEnd, newEnd)
 *   - newEnd   < oldEnd   → le bloc se réduit en aval → reopenZones += [newEnd, oldEnd)
 *
 * Chaque zone = [from, toExclusive] (Convention A). ≤ 2 zones par type.
 *   - closeZones → à pousser numAvail=0 (fermer la dispo, extension du bloc)
 *   - reopenZones → à pousser numAvail=1 (rouvrir la dispo, réduction du bloc)
 *
 * ⚠️ INVARIANT d'usage : ces règles supposent des plages qui se CHEVAUCHENT ou
 * se TOUCHENT (édition « réduire/étendre »). Sur une relocalisation totalement
 * DISJOINTE (new entièrement avant/après old, avec trou), close et reopen
 * peuvent se recouvrir → le caller doit borner ce cas en amont (le handler fige
 * `new_start = block.start_date` sur un bloc démarré). Sans borne côté caller,
 * l'appelant NE doit pas relocaliser un bloc de façon disjointe.
 *
 * @param {string} oldStart ISO YYYY-MM-DD (inclusif)
 * @param {string} oldEnd   ISO YYYY-MM-DD (Convention A exclusif)
 * @param {string} newStart ISO YYYY-MM-DD (inclusif)
 * @param {string} newEnd   ISO YYYY-MM-DD (Convention A exclusif)
 * @returns {{closeZones:Array<[string,string]>, reopenZones:Array<[string,string]>, noop:boolean}}
 */
export function computeDatesDiff(oldStart, oldEnd, newStart, newEnd) {
  const closeZones = [];
  const reopenZones = [];

  // Bord amont (start).
  if (newStart < oldStart) {
    closeZones.push([newStart, oldStart]);
  } else if (newStart > oldStart) {
    reopenZones.push([oldStart, newStart]);
  }

  // Bord aval (end).
  if (newEnd > oldEnd) {
    closeZones.push([oldEnd, newEnd]);
  } else if (newEnd < oldEnd) {
    reopenZones.push([newEnd, oldEnd]);
  }

  return {
    closeZones,
    reopenZones,
    noop: closeZones.length === 0 && reopenZones.length === 0,
  };
}

/**
 * Mappe un logement_id format "pricing" vers le format "reservations".
 *
 * **Convention prod réelle (ADR-098, PR #165 hotfix)** : la table
 * `logement_pricing_config` stocke les ids en format `reservations` direct
 * (`lc1`, `lc3`, `l1`...) — vérifié empiriquement post-merge PR #163. Le
 * helper accepte donc 2 formats :
 *  - Format pricing canonique (rétrocompat hypothèse initiale BPR5-3) :
 *      'p1'..'p9'   → 'l1'..'l9'   (strip 'p'→'l')
 *      'p6.lc1..3'  → 'lc1..3'     (strip 'p6.')
 *      'p6'         → throw 'logement_p6_not_blockable_use_subloc'
 *  - Format reservations direct (DB prod réelle) :
 *      'l1'..'l9'   → identity
 *      'lc1'..'lc3' → identity
 *
 * Garde `p6` préservée défensive : Maison Zen entier non bloquable
 * (sémantique métier — 3 chambres réservables séparément).
 *
 * **Maintenance future** : Si nouveaux logements ajoutés (p10, p11+) →
 * étendre regex `^p(\d+)$` ET `^l(\d+)$`, idem cas sous-loc associés.
 *
 * @param {string} pricingId
 * @returns {string} reservations.logement_id format
 * @throws Error('logement_id_unknown_format') si format invalide
 * @throws Error('logement_p6_not_blockable_use_subloc') si p6 nu
 */
export function mapPricingIdToReservationId(pricingId) {
  if (typeof pricingId !== "string" || !pricingId) {
    throw new Error("logement_id_unknown_format");
  }
  if (pricingId === "p6") {
    throw new Error("logement_p6_not_blockable_use_subloc");
  }
  if (/^p6\.lc[1-3]$/.test(pricingId)) {
    return pricingId.slice(3);
  }
  const m = pricingId.match(/^p([1-9])$/);
  if (m) return `l${m[1]}`;
  // ADR-098 : DB prod stocke en format reservations direct → identity.
  if (/^lc[1-3]$/.test(pricingId)) return pricingId;
  if (/^l[1-9]$/.test(pricingId)) return pricingId;
  throw new Error("logement_id_unknown_format");
}

// ─────────────────────────────────────────────────────────────────────────
// Validateurs guest_fields pricing_blocks — déplacés depuis api/beds24-mapping.js
// (chantier A.1 LOT 1). ⚠️ ADR-021 : jumeau CJS lib/pricing-block.js DOIT rester
// identique. Backend-only à ce jour (le front n'importe pas encore ces helpers).
// ─────────────────────────────────────────────────────────────────────────


// Helper validation email basique (cohérent reservations.guest_email existant).
export function isValidEmailOrEmpty(email) {
  if (email == null || email === "") return true;
  if (typeof email !== "string") return false;
  if (email.length > 200) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// Whitelist 14 champs voyageur acceptés sur pricing_blocks UPDATE.
// Anti-mass-assignment ADR-094 — toute clé hors whitelist → 400.
export const PRICING_BLOCK_GUEST_WHITELIST = [
  "guest_first_name",
  "guest_last_name",
  "guest_email",
  "guest_phone",
  "guest_address",
  "guest_city",
  "guest_postal_code",
  "guest_country",
  "guest_language",
  "guest_count",
  "children_count",
  "notes",
  "total_amount",
];

// PR #169a v1.4 Commit E (fix NQAa-1) — Helper validation pricing_blocks guest_fields.
// DRY : partagé entre handlePricingBlockCreate (résa direct interne FBZ) et
// handlePricingBlockUpdateInternal (édition voyageur post-création).
//
// Retour :
//   { ok: true,  clean: {...} }  → payload validé prêt pour INSERT/UPDATE
//   { ok: false, error: "code" } → erreur 400 explicite (whitelist OU champ invalide)
//
// Sémantique :
//   - null/undefined par champ → accepté (clear field)
//   - chaîne vide TEXT → normalisée à null
//   - email regex + ≤200, language enum, count/children/total ranges, notes ≤500
//   - PAS de side effect — retourne juste structure validation.
export function validateGuestFields(guest_fields) {
  if (!guest_fields || typeof guest_fields !== "object") {
    return { ok: false, error: "guest_fields_required" };
  }
  const incomingKeys = Object.keys(guest_fields);
  const invalidKeys = incomingKeys.filter(
    (k) => !PRICING_BLOCK_GUEST_WHITELIST.includes(k),
  );
  if (invalidKeys.length > 0) {
    return { ok: false, error: "invalid_fields", details: invalidKeys };
  }
  const clean = {};
  for (const key of incomingKeys) {
    const val = guest_fields[key];
    if (val === null || val === undefined) {
      clean[key] = null;
      continue;
    }
    switch (key) {
      case "guest_email":
        if (!isValidEmailOrEmpty(val)) {
          return { ok: false, error: "guest_email_invalid" };
        }
        clean[key] = val;
        break;
      case "guest_language":
        if (val !== "" && !["fr", "en", "es"].includes(val)) {
          return { ok: false, error: "guest_language_invalid" };
        }
        clean[key] = val === "" ? null : val;
        break;
      case "guest_count": {
        const n = Number(val);
        if (!Number.isInteger(n) || n < 0 || n > 50) {
          return { ok: false, error: "guest_count_invalid" };
        }
        clean[key] = n;
        break;
      }
      case "children_count": {
        const n = Number(val);
        if (!Number.isInteger(n) || n < 0 || n > 20) {
          return { ok: false, error: "children_count_invalid" };
        }
        clean[key] = n;
        break;
      }
      case "total_amount": {
        const n = Number(val);
        if (!Number.isFinite(n) || n < 0 || n > 99999.99) {
          return { ok: false, error: "total_amount_invalid" };
        }
        clean[key] = n;
        break;
      }
      case "notes":
        if (typeof val !== "string" || val.length > 500) {
          return { ok: false, error: "notes_too_long" };
        }
        clean[key] = val;
        break;
      default:
        // TEXT fields : max 200 chars.
        if (typeof val !== "string" || val.length > 200) {
          return { ok: false, error: `${key}_invalid` };
        }
        clean[key] = val === "" ? null : val;
    }
  }
  return { ok: true, clean, incomingKeys };
}